import { useCenterStore } from '@caradasstore/CenterStore'
import { useBillingStore } from '@caradasstore/BillingStore'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { ChevronLeft } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CONTACT_US } from '../../data/billingData'
import { requestDowngrade } from '../../api/billingApi'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { isTrialPackage, tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { OutlineButton, StatusBanner } from './BillingUi'
import { PlanComparison } from './PlanComparison'
import { PromoCodeField, promoCovers } from './PromoCodeField'
import { SubscriptionOverview } from './SubscriptionOverview'
import { deriveBillingState, formatBillingDate, trialRefusalMessage } from './billingView'

// States that have a known plan, so the comparison marks the "current" card.
const STATES_WITH_PLAN = ['active', 'trialing', 'past_due']

// Why the browser came back to the tab, from Stripe's redirect params.
const returnReasonFrom = (params) => {
  if (params.get('checkout') === 'success') return 'checkout'
  if (params.get('checkout') === 'cancel') return 'cancel'
  if (params.get('billing') === 'updated') return 'portal'
  return null
}

// The same URL without the one-shot Stripe return params, so a refresh or Back does not re-sync.
const withoutReturnParams = (location, params) => {
  const next = new URLSearchParams(params)
  next.delete('checkout')
  next.delete('billing')
  const search = next.toString()
  return `${location.pathname}${search ? `?${search}` : ''}`
}

// Post-Checkout banner, driven by what the sync actually found — not by the URL param alone.
const CheckoutResultBanner = ({ catalog, subscription, state, onRefresh }) => {
  const planName = tierById(catalog, subscription.tier)?.name ?? 'Your plan'
  // A Free Trial that was refused (card, account or location already had one): the $1 came back.
  // The overview below already says so — show nothing here rather than "being activated".
  if (!subscription.hasSubscription && trialRefusalMessage(subscription.trialRefusal)) {
    return null
  }
  if (subscription.stripeBacked && state === 'active') {
    return <StatusBanner tone='green' label='Payment' title={`Thanks! Your payment was received — ${planName} is now active.`} />
  }
  // The Free Trial package: a one-time payment, then the app's own 30-day trial (no Stripe subscription).
  if (isTrialPackage(tierById(catalog, subscription.tier)) && state === 'trialing') {
    return (
      <StatusBanner
        tone='green'
        label='Payment'
        title={`Thanks! Your payment was received — your ${planName} is active until ${formatBillingDate(subscription.trialEndDate)}.`}
      />
    )
  }
  if (subscription.stripeBacked && state === 'trialing') {
    return (
      <StatusBanner
        tone='green'
        label='Payment method'
        title={`Thanks! Your card is saved — ${planName} starts automatically on ${formatBillingDate(subscription.trialEndDate)}.`}
      />
    )
  }
  return (
    <StatusBanner
      tone='amber'
      label='Payment'
      title='Thanks! Your payment was received — your subscription is being activated.'
      body='Stripe is still finalizing it. This usually takes a few seconds; if nothing changes, contact us.'
      action={<OutlineButton onClick={onRefresh}>Refresh</OutlineButton>}
    />
  )
}
CheckoutResultBanner.propTypes = {
  catalog: PropTypes.object,
  subscription: PropTypes.shape({
    tier: PropTypes.string,
    stripeBacked: PropTypes.bool,
    trialEndDate: PropTypes.any,
    hasSubscription: PropTypes.bool,
    trialRefusal: PropTypes.string
  }).isRequired,
  state: PropTypes.string.isRequired,
  onRefresh: PropTypes.func.isRequired
}

/**
 * The Billing tab (Settings → Billing). Renders inside the Settings right pane; the page
 * supplies the header. Two views: the state-driven subscription Overview (all six PRD §6.2
 * states, from GET /v1/billing/subscription) and the Plan Comparison.
 *
 * Self-serve: subscribe / reactivate / convert a trial through Stripe Checkout, upgrade
 * in-app (prorated), update the card or cancel in the Stripe Portal — any time, since plans
 * are month-to-month — and request a downgrade through a pre-filled support ticket. Contact Us
 * stays for Network accounts.
 *
 * Stripe sends the browser back with ?checkout=success|cancel after Checkout, or
 * ?billing=updated after the Customer Portal.
 */
export const BillingSettings = () => {
  const history = useHistory()
  const location = useLocation()
  const ccId = useCenterStore((store) => store.ccId)
  const {
    subscription,
    invoices,
    isLoading,
    error,
    loadedForCcId,
    portalPending,
    portalError,
    checkoutPending,
    checkoutError,
    upgradePending,
    upgradeError,
    upgradeMessage,
    loadBilling,
    openPortal,
    startCheckout,
    upgrade,
    endTrial,
    cancelDowngrade,
    syncFromStripe,
    clearNotices
  } = useBillingStore()
  // The plan catalog (names, prices, features, order) comes from the database via the API.
  const { catalog, status: catalogStatus, reload: reloadCatalog } = useCatalog()
  const [view, setView] = useState('overview')
  // A promo code, for a location that has never had a package (the backend re-checks it at Checkout).
  const [promo, setPromo] = useState(null)
  // One-shot notice for the visit that returned from Stripe; survives the URL clean-up below.
  const [returnNotice, setReturnNotice] = useState(null)
  const consumedReturn = useRef(false)

  const params = new URLSearchParams(location?.search || '')
  const returnReason = returnReasonFrom(params)

  // Back from Checkout (?checkout=success|cancel) or the Portal (?billing=updated): remember why,
  // drop the params from the URL, and reconcile from Stripe first so the new state shows at once.
  // Otherwise a plain load. The URL clean-up re-runs this effect once; that run is skipped.
  useEffect(() => {
    if (returnReason && !consumedReturn.current) {
      consumedReturn.current = true
      setReturnNotice(returnReason)
      history.replace(withoutReturnParams(location, params))
      if (returnReason === 'cancel') {
        loadBilling()
      } else {
        syncFromStripe()
      }
      return
    }
    if (consumedReturn.current) {
      consumedReturn.current = false
      return
    }
    loadBilling()
  }, [ccId, returnReason, loadBilling, syncFromStripe])

  const goContact = () => history.push(CONTACT_US.href)
  // Downgrades are handled by our team: file the request (it carries a support ticket) straight from here, so a
  // plan without the Support feature can still ask. The request then shows on Billing with a cancel button.
  const [downgradeNotice, setDowngradeNotice] = useState(null) // { tone, text }
  const goDowngrade = async (toTier) => {
    const to = tierById(catalog, toTier)
    try {
      await requestDowngrade(ccId, toTier)
      setDowngradeNotice({
        tone: 'amber',
        text: `Downgrade to ${to?.name || toTier} requested. Our team will make the change; you can cancel the request below.`
      })
      setView('overview')
      loadBilling({ force: true })
    } catch (err) {
      setDowngradeNotice({ tone: 'red', text: getFriendlyErrorMessage(err) })
    }
  }
  const state = deriveBillingState(subscription)
  const currentTierKey = STATES_WITH_PLAN.includes(state) ? subscription?.tier : undefined
  const stale = loadedForCcId !== ccId
  const pending = portalPending || checkoutPending || upgradePending
  const actionError = portalError || checkoutError || upgradeError

  if (catalogStatus === 'error') {
    return (
      <StatusBanner
        tone='red'
        label='Billing'
        title='We couldn’t load the plans.'
        body='Check your connection and try again.'
        action={<OutlineButton onClick={reloadCatalog}>Try again</OutlineButton>}
      />
    )
  }

  if (error) {
    return (
      <StatusBanner
        tone='red'
        label='Billing'
        title='We couldn’t load your billing details.'
        body={error}
        action={<OutlineButton onClick={loadBilling}>Try again</OutlineButton>}
      />
    )
  }

  if (isLoading || !subscription || stale || catalogStatus !== 'ready') {
    return (
      <AdasBox className='rounded-xl border border-[#D1D5D8] bg-white shadow-sm px-8 py-10 text-center' aria-busy='true'>
        <AdasTypography className='text-[14px] text-muted-foreground'>Loading billing…</AdasTypography>
      </AdasBox>
    )
  }

  const promoEligible = state === 'none' && !subscription.managedExternally
  const checkout = (tier) => (promoCovers(promo, tierById(catalog, tier)) ? startCheckout(tier, promo.code) : startCheckout(tier))
  const promoField = promoEligible && (
    <AdasBox className='rounded-xl border border-[#D1D5D8] bg-white shadow-sm px-6 py-4'>
      <PromoCodeField ccId={ccId} applied={promo} onApplied={setPromo} disabled={pending} />
    </AdasBox>
  )

  const comparisonProps = {
    catalog,
    currentTierKey,
    hasStripeSubscription: Boolean(subscription.stripeBacked),
    pending,
    downgradeInProgress: Boolean(subscription.planChange),
    onCheckout: checkout,
    onUpgrade: upgrade,
    onDowngrade: goDowngrade
  }

  return (
    <AdasBox className='flex flex-col gap-4'>
      {returnNotice === 'checkout' && (
        <CheckoutResultBanner catalog={catalog} subscription={subscription} state={state} onRefresh={syncFromStripe} />
      )}
      {returnNotice === 'cancel' && <StatusBanner tone='slate' label='Checkout' title='Checkout was canceled — no changes were made.' />}
      {returnNotice === 'portal' && (
        <StatusBanner
          tone='slate'
          label='Billing portal'
          title='You’re all set — any changes you made in the billing portal will appear here shortly.'
        />
      )}
      {downgradeNotice && (
        <StatusBanner
          tone={downgradeNotice.tone}
          label='Downgrade'
          title={downgradeNotice.text}
          action={<OutlineButton onClick={() => setDowngradeNotice(null)}>Dismiss</OutlineButton>}
        />
      )}
      {upgradeMessage && (
        <StatusBanner
          tone='amber'
          label='Upgraded'
          title={upgradeMessage}
          action={<OutlineButton onClick={clearNotices}>Dismiss</OutlineButton>}
        />
      )}
      {actionError && (
        <StatusBanner
          tone='red'
          label='Billing'
          title='That didn’t go through.'
          body={actionError}
          action={<OutlineButton onClick={clearNotices}>Dismiss</OutlineButton>}
        />
      )}

      {view === 'compare' ? (
        <AdasBox className='flex flex-col gap-4'>
          <AdasBox>
            <OutlineButton onClick={() => setView('overview')}>
              <ChevronLeft className='-ml-1 mr-1 h-4 w-4' />
              Back to billing
            </OutlineButton>
          </AdasBox>
          {promoField}
          <PlanComparison {...comparisonProps} />
        </AdasBox>
      ) : (
        <>
          {promoField}
          <SubscriptionOverview
            catalog={catalog}
            state={state}
            subscription={subscription}
            invoices={invoices}
            pending={pending}
            onCompare={() => setView('compare')}
            onContact={goContact}
            onManageBilling={openPortal}
            onCheckout={checkout}
            onUpgrade={upgrade}
            onEndTrial={endTrial}
            onDowngrade={goDowngrade}
            onCancelDowngrade={cancelDowngrade}
          />
        </>
      )}
    </AdasBox>
  )
}
